import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  AppLayout,
  Header,
  Container,
  SpaceBetween,
  Button,
  Box,
  Badge,
  ColumnLayout,
  StatusIndicator,
  Table,
  TextFilter,
  Pagination,
  ProgressBar,
  Alert,
  Cards,
  Link,
} from "@cloudscape-design/components";
import TopNavigation from "./containers/TopNavigation/TopNavigation";
import Sidebar from "./containers/Sidebar/Sidebar";
import RightSidebar from "./containers/RightSidebar/RightSidebar";
import { ThemeSelector } from "./components/ThemeSelector/ThemeSelector";
import { applyTheme, getCurrentTheme, Theme } from "./themes/themeConfig";
import "./themes/global.themed.css";
import "./themes/navigation-force.css";
import HomePage from "./containers/homePage/HomePage";
import MonitorAlert from "./containers/MonitorAlert/MonitorAlertReal";
import IntelligentAnalysisReports from "./containers/IntelligentAnalysisReports/IntelligentAnalysisReports";
import ProcessOptimization from "./containers/ProcessOptimization/ProcessOptimization";
import ExecutiveOverview from "./containers/ExecutiveOverview/ExecutiveOverview";
import ConfigurationSetting from "./containers/ConfigurationSetting/ConfigurationSetting";
import Program from "./containers/Program/Program";
import PermissionManagement from "./containers/PermissionManagement/PermissionManagement";
import { Hub } from "@aws-amplify/core";
import {
  getUserInfo,
  redirectToLoginPage,
  initializeSession,
} from "./utils/auth-provider";
import { useDispatch, useSelector } from "react-redux";
import { pingRequest } from "./redux/actions/apiActions";
import { setUserInfo, clearUserInfo } from "./redux/actions/userActions";
import { RootState } from "./redux/reducers/rootReducer";
import { ACCESS_ALLOWLIST, normalizeLogin } from "./config/accessAllowlist";
import { config } from "./config/config";

// Protected Route Component - checks allowlist access and redirects to home if unauthorized
const ProtectedRoute: React.FC<{ allowlistKey: string; children: React.ReactElement }> = ({
  allowlistKey,
  children,
}) => {
  const userName = useSelector((state: RootState) => state.user.userName);
  const allowlist = ACCESS_ALLOWLIST[allowlistKey];
  // If no allowlist is defined for this key, allow access by default
  if (!allowlist) return children;
  // Check if user is in the allowlist (normalize login to strip @domain for bot accounts)
  if (allowlist.includes(normalizeLogin(userName))) return children;
  // Unauthorized - redirect to home
  return <Navigate to="/" replace />;
};

// App Content Component with Router
const AppContent: React.FC = () => {
  const location = useLocation();
  const [chatState, setChatState] = useState<"closed" | "default" | "expanded">(
    // Set initial chatState based on the landing route
    location.pathname === "/analysis" || location.pathname === "/reports"
      ? "expanded"
      : "closed",
  );
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());
  const prevPathRef = React.useRef<string>(location.pathname);

  const handleChatStateChange = (
    newState: "closed" | "default" | "expanded",
  ) => {
    setChatState(newState);
  };

  useEffect(() => {
    const updateTheme = () => {
      const theme = getCurrentTheme();
      setCurrentTheme(theme);
      applyTheme(theme);

      // Apply theme to body and root
      document.body.style.backgroundColor = theme.colors.mainBackground;
      document.body.style.color = theme.colors.textPrimary;
    };

    // Listen for theme changes
    window.addEventListener("themeChanged", updateTheme);

    // Apply initial theme
    updateTheme();

    return () => {
      window.removeEventListener("themeChanged", updateTheme);
    };
  }, []);

  // Auto-expand chatbot on specific routes
  useEffect(() => {
    // Only trigger on actual route changes, not on state changes
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;

      if (
        location.pathname === "/analysis" ||
        location.pathname === "/reports"
      ) {
        setChatState("expanded");
      } else if (location.pathname === "/optimization" && chatState === "expanded") {
        setChatState("default");
      } else {
        // Collapse the right sidebar completely on all other pages
        setChatState("closed");
      }
    }
  }, [location.pathname]);

  return (
    <div
      className="awsui"
      style={{
        backgroundColor: currentTheme.colors.mainBackground,
        color: currentTheme.colors.textPrimary,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}
      >
        <TopNavigation />
      </div>

      <div
        id="app-main-scroll-container"
        style={{
          display: "flex",
          flex: 1,
          overflow: "auto",
          position: "relative",
          marginTop: "56px", // Height of TopNavigation
        }}
      >
        <div
          style={{
            flex: 1,
            transition: "margin-right 0.3s ease-out",
            marginRight:
              chatState === "closed"
                ? "50px" // Leave space for Open AI button
                : chatState === "default"
                  ? "500px"
                  : "0px",
          }}
        >
          <AppLayout
            navigation={<Sidebar />}
            navigationOpen={navigationOpen}
            onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
            contentType="table"
            toolsHide={true}
            content={
              <div
                style={{
                  backgroundColor: currentTheme.colors.cardBackground,
                  color: currentTheme.colors.textPrimary,
                  minHeight: "100%",
                  width: "100%",
                  transition: "width 0.3s ease-out",
                  overflow: "visible",
                }}
              >
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/overview" element={<ExecutiveOverview />} />
                  <Route path="/programs" element={<Program />} />
                  <Route
                    path="/monitor"
                    element={
                      <ProtectedRoute allowlistKey="monitorAlert">
                        <MonitorAlert />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analysis"
                    element={<IntelligentAnalysisReports initialView="main" />}
                  />
                  <Route
                    path="/reports"
                    element={<IntelligentAnalysisReports initialView="reports" />}
                  />
                  <Route
                    path="/optimization"
                    element={<ProcessOptimization />}
                  />
                  <Route
                    path="/configuration"
                    element={<ConfigurationSetting />}
                  />
                  {config.STAGE !== "gamma" && config.STAGE !== "prod" && (
                    <Route
                      path="/permissions"
                      element={<PermissionManagement />}
                    />
                  )}
                </Routes>
              </div>
            }
          />
        </div>

        <RightSidebar
          chatState={chatState}
          onChatStateChange={handleChatStateChange}
          navigationOpen={navigationOpen}
        />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const [authState, setAuthState] = useState<string>("UNAUTHENTICATED");
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const hasInitialized = React.useRef<boolean>(false);
  const pendingCustomState = React.useRef<string | null>(null);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Initialize theme system on app startup
    const currentTheme = getCurrentTheme();
    applyTheme(currentTheme);

    // Apply initial body styling
    document.body.style.backgroundColor = currentTheme.colors.mainBackground;
    document.body.style.color = currentTheme.colors.textPrimary;
    document.body.style.fontFamily = currentTheme.colors.textPrimary
      ? "Amazon Ember, Helvetica Neue, Roboto, Arial, sans-serif"
      : "inherit";

    // Try to get user info on mount
    const handleUserSignIn = async () => {
      // Prevent multiple redirects
      if (isRedirecting) {
        return;
      }

      // Check if we're in OAuth callback flow
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has("code");

      if (hasCode) {
        return; // Don't call getUserInfo yet, wait for Hub signIn event
      }

      try {
        const userInfo = await getUserInfo();

        if (userInfo) {
          setAuthState("AUTHENTICATED");

          // Store user info in Redux (including tokens and expiration)

          // Determine username based on provider
          const providerName = userInfo.identities?.[0]?.providerName || "";
          let username = "";

          if (providerName === "LASSO") {
            // LASSO: use custom:username attribute
            username = userInfo["custom:username"] || "";
          } else if (providerName === "AmazonFederate") {
            // AmazonFederate: extract from email
            username = userInfo.email ? userInfo.email.split("@")[0] : "";
          } else {
            // Fallback for other providers
            username =
              userInfo.preferred_username ||
              userInfo.identities?.[0]?.userId ||
              "";
          }

          dispatch(
            setUserInfo({
              username: username,
              email: userInfo.email || "",
              sub: userInfo.sub,
              preferredUsername: userInfo.preferred_username,
              cognitoUsername: userInfo["cognito:username"],
              idToken: userInfo.idToken,
              accessToken: userInfo.accessToken,
              refreshToken: userInfo.refreshToken,
              tokenExpiration: userInfo.tokenExpiration,
              authTime: userInfo.authTime,
              familyName: userInfo.family_name || userInfo.familyName || "",
              givenName: userInfo.given_name || userInfo.givenName || "",
              provider: providerName,
            }),
          );

          // Initialize session - DISABLED: We only use access token for API calls
          // Session timer was causing page refresh every 30 seconds
          // await initializeSession(
          //   () => {},
          //   (willExpire) => {},
          //   (user) => {},
          // );

          // Test ping endpoint
          dispatch(pingRequest());
        } else {
          setAuthState("UNAUTHENTICATED");
          dispatch(clearUserInfo());
          setIsRedirecting(true);
          redirectToLoginPage();
        }
      } catch (error) {
        console.error("[App] ERROR in handleUserSignIn:", error);
        setAuthState("UNAUTHENTICATED");
        dispatch(clearUserInfo());
        setIsRedirecting(true);
        redirectToLoginPage();
      }
    };

    // Listen to auth events
    Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
          console.log("[App] Hub signIn event received");
          setAuthState("AUTHENTICATED");
          handleUserSignIn().then(() => {
            // After sign-in is complete, navigate to the custom state URL if pending
            if (pendingCustomState.current) {
              const targetUrl = pendingCustomState.current;
              pendingCustomState.current = null;
              console.log("[App] Navigating to pending customOAuthState URL:", targetUrl);
              // Only navigate if the target URL is different from current (avoid unnecessary reload)
              if (targetUrl !== window.location.href) {
                window.location.href = targetUrl;
              }
            }
          });
          break;
        case "signIn_failure":
          setAuthState("SIGNIN_FAILURE");
          dispatch(clearUserInfo());
          break;
        case "signOut":
          dispatch(clearUserInfo());
          redirectToLoginPage();
          break;
        case "customOAuthState":
          // Store the custom state URL - don't redirect immediately!
          // The redirect will happen after signIn event is fully processed
          // to avoid interrupting the OAuth token exchange.
          console.log("[App] customOAuthState received:", decodeURIComponent(data));
          pendingCustomState.current = decodeURIComponent(data);
          break;
      }
    });

    handleUserSignIn();
  }, [dispatch]); // IMPORTANT: Do NOT include isRedirecting in dependencies to avoid loops

  // Only render the app content after authentication is complete.
  // This prevents child components (like ConfigurationSetting) from dispatching
  // API calls before the token/username are stored in Redux.
  if (authState !== "AUTHENTICATED") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          fontFamily: "Amazon Ember, Helvetica Neue, Roboto, Arial, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e0e0e0",
              borderTopColor: "#0073bb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: "#666", fontSize: "14px" }}>Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
